import clsx from "clsx";
import {
  CSSProperties,
  ElementType,
  ReactElement,
  UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useEvent from "react-use-event-hook";
import { CellComponentProps, Grid, useGridCallbackRef } from "react-window";

import { usePageScrollPositionMemory, useRtl } from "@ryde/redux";
import {
  getScrollbarWidth,
  ScrollAreaEnum,
  VIRTUALIZED_GRID_OVERSCAN_COUNT,
} from "@ryde/shared";

import { VirtualizedGridStyled } from "./virtualized-grid.styled";

// https://gearheart.io/articles/smooth-react-virtual-scroll-with-fixed-rows-columns/
// http://web.archive.org/web/20231003145612/https://gearheart.io/articles/smooth-react-virtual-scroll-with-fixed-rows-columns/
interface IVirtualizedGridProps {
  className?: string;
  columnWidth: (index: number) => number;
  rowHeight: (index: number) => number;
  columnCount: number;
  rowCount: number;
  width: number;
  height: number;

  frozenRowCount: number;
  frozenColumnCount: number;

  hideScrollbars?: boolean;

  scrollArea: ScrollAreaEnum;

  gridData?: unknown;
  CellRenderer: ElementType;
}

interface IOffsetCellProps {
  colOffset: number;
  rowOffset: number;
  CellRenderer: ElementType;
  gridData: unknown;
}

const OffsetCellRenderer = ({
  rowOffset,
  colOffset,
  CellRenderer,
  gridData,
  columnIndex,
  rowIndex,
  style,
}: CellComponentProps<IOffsetCellProps>): ReactElement => (
  <CellRenderer
    columnIndex={columnIndex + colOffset}
    rowIndex={rowIndex + rowOffset}
    style={style}
    data={gridData}
  />
);

export const VirtualizedGrid = (props: IVirtualizedGridProps): ReactElement => {
  const { frozenRowCount, frozenColumnCount, className, scrollArea } = props;
  const { rowHeight, columnWidth } = props;
  const { gridData, CellRenderer } = props;

  const [topHeaderApi, setTopHeaderApi] = useGridCallbackRef();
  const [leftHeaderApi, setLeftHeaderApi] = useGridCallbackRef();
  const [mainGridApi, setMainGridApi] = useGridCallbackRef();

  const mainElement = mainGridApi?.element ?? null;
  const topElement = topHeaderApi?.element ?? null;
  const leftElement = leftHeaderApi?.element ?? null;

  const [outerContainer, setOuterContainer] = useState<HTMLDivElement | null>(
    null,
  );
  const isSyncScrollingRef = useRef(false);
  const { isRtl, direction } = useRtl();
  const scrollBarWidth = useMemo(() => getScrollbarWidth(), []);

  const scrollGridsTo = useCallback(
    (scrollLeft: number, scrollTop: number): void => {
      isSyncScrollingRef.current = true;

      if (mainElement) {
        mainElement.scrollTo({ left: scrollLeft, top: scrollTop });
      }

      if (topElement) {
        topElement.scrollTo({ left: scrollLeft });
      }

      if (leftElement) {
        leftElement.scrollTo({ top: scrollTop });
      }

      // Keep sync guard through the scroll event cascade (one rAF was too short).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          isSyncScrollingRef.current = false;
        });
      });
    },
    [leftElement, mainElement, topElement],
  );

  const syncFrozenPanesToMainPane = useEvent((): void => {
    if (!mainElement) {
      return;
    }

    if (leftElement) {
      leftElement.scrollTop = mainElement.scrollTop;
    }

    if (topElement) {
      topElement.scrollLeft = mainElement.scrollLeft;
    }
  });

  usePageScrollPositionMemory({
    element: mainElement,
    area: scrollArea,
    intentElement: outerContainer,
    onApplied: syncFrozenPanesToMainPane,
  });

  useEffect(() => {
    if (mainElement?.scrollTop || mainElement?.scrollLeft) {
      return;
    }

    scrollGridsTo(0, 0);
  }, [mainElement, scrollGridsTo]);

  const frozenRowHeight = useMemo(() => {
    let height = 0;

    for (let i = 0; i < frozenRowCount; i++) {
      height += rowHeight(i);
    }

    return height;
  }, [frozenRowCount, rowHeight]);

  const frozenColWidth = useMemo(() => {
    let width = 0;

    for (let i = 0; i < frozenColumnCount; i++) {
      width += columnWidth(i);
    }

    return width;
  }, [columnWidth, frozenColumnCount]);

  const unfrozenHeight = props.height - frozenRowHeight;
  const unfrozenWidth = props.width - frozenColWidth;
  const unfrozenTopGridWidth = props.width - frozenColWidth - scrollBarWidth;

  const offsetColumnWidth = useCallback(
    (index: number) => columnWidth(index + frozenColumnCount),
    [columnWidth, frozenColumnCount],
  );
  const offsetRowHeight = useCallback(
    (index: number) => rowHeight(index + frozenRowCount),
    [frozenRowCount, rowHeight],
  );

  const cornerCellProps = useMemo(
    (): IOffsetCellProps => ({
      colOffset: 0,
      rowOffset: 0,
      CellRenderer,
      gridData,
    }),
    [CellRenderer, gridData],
  );

  const frozenRowCellProps = useMemo(
    (): IOffsetCellProps => ({
      colOffset: frozenColumnCount,
      rowOffset: 0,
      CellRenderer,
      gridData,
    }),
    [frozenColumnCount, CellRenderer, gridData],
  );

  const frozenColumnCellProps = useMemo(
    (): IOffsetCellProps => ({
      colOffset: 0,
      rowOffset: frozenRowCount,
      CellRenderer,
      gridData,
    }),
    [frozenRowCount, CellRenderer, gridData],
  );

  const mainCellProps = useMemo(
    (): IOffsetCellProps => ({
      colOffset: frozenColumnCount,
      rowOffset: frozenRowCount,
      CellRenderer,
      gridData,
    }),
    [frozenColumnCount, frozenRowCount, CellRenderer, gridData],
  );

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>): void => {
      if (isSyncScrollingRef.current) {
        return;
      }

      const { scrollLeft, scrollTop } = event.currentTarget;

      if (topElement) {
        topElement.scrollTo({ left: scrollLeft });
      }

      if (leftElement) {
        leftElement.scrollTo({ top: scrollTop });
      }
    },
    [leftElement, topElement],
  );

  const handleVerticalScroll = useCallback(
    (event: UIEvent<HTMLDivElement>): void => {
      if (isSyncScrollingRef.current) {
        return;
      }

      if (mainElement) {
        mainElement.scrollTo({ top: event.currentTarget.scrollTop });
      }
    },
    [mainElement],
  );

  const [gridHeight, gridWidth] = useMemo(() => {
    let height = 0;
    let width = 0;

    for (let i = 0; i < props.rowCount; i++) {
      height += rowHeight(i);
    }

    for (let i = 0; i < props.columnCount; i++) {
      width += columnWidth(i);
    }

    return [height, width];
  }, [columnWidth, rowHeight, props.columnCount, props.rowCount]);

  const maxVScroll = gridHeight - frozenRowHeight - unfrozenHeight;
  const maxHScroll = gridWidth - frozenColWidth - unfrozenWidth;

  useEffect(() => {
    if (!outerContainer) {
      return;
    }

    const handler = (e: WheelEvent): void => {
      e.preventDefault();

      if (!mainElement) {
        return;
      }

      const { deltaX, deltaY } = e;
      const scrollLeft = Math.min(
        maxHScroll,
        Math.abs(mainElement.scrollLeft) + (isRtl ? -deltaX : deltaX),
      );
      const scrollTop = Math.min(maxVScroll, mainElement.scrollTop + deltaY);

      scrollGridsTo(scrollLeft, scrollTop);
    };

    outerContainer.addEventListener("wheel", handler);

    return () => outerContainer.removeEventListener("wheel", handler);
  }, [
    isRtl,
    mainElement,
    maxHScroll,
    maxVScroll,
    outerContainer,
    scrollGridsTo,
  ]);

  const unfrozenHorizontalDivStyles = useMemo((): CSSProperties => {
    const result: CSSProperties = {
      insetInlineStart: frozenColWidth,
      top: 0,
    };
    result.marginInlineEnd = scrollBarWidth;
    return result;
  }, [frozenColWidth, scrollBarWidth]);

  const mainGridInnerContainerStyles = useMemo(
    (): CSSProperties => ({
      insetInlineStart: frozenColWidth,
      top: frozenRowHeight,
    }),
    [frozenColWidth, frozenRowHeight],
  );

  return (
    <VirtualizedGridStyled
      ref={setOuterContainer}
      className={clsx("VirtualizedGrid", className)}
    >
      <div className="VirtualizedGrid-FirstRowContainer">
        <Grid
          style={{ height: frozenRowHeight, width: frozenColWidth }}
          columnCount={frozenColumnCount}
          rowCount={frozenRowCount}
          columnWidth={columnWidth}
          rowHeight={rowHeight}
          className="VirtualizedGrid-CornerGrid"
          cellComponent={OffsetCellRenderer}
          cellProps={cornerCellProps}
          overscanCount={VIRTUALIZED_GRID_OVERSCAN_COUNT}
          dir={direction}
        />
        <div
          className="VirtualizedGrid-TopGridContainer"
          style={unfrozenHorizontalDivStyles}
        >
          <Grid
            gridRef={setTopHeaderApi}
            style={{
              height: frozenRowHeight,
              width: unfrozenTopGridWidth,
            }}
            columnCount={props.columnCount - frozenColumnCount}
            rowCount={frozenRowCount}
            columnWidth={offsetColumnWidth}
            rowHeight={rowHeight}
            className="VirtualizedGrid-TopGrid"
            cellComponent={OffsetCellRenderer}
            cellProps={frozenRowCellProps}
            overscanCount={VIRTUALIZED_GRID_OVERSCAN_COUNT}
            dir={direction}
          />
        </div>
      </div>
      <div className="VirtualizedGrid-FirstColumnContainer">
        <Grid
          gridRef={setLeftHeaderApi}
          style={{ height: unfrozenHeight, width: frozenColWidth }}
          onScroll={handleVerticalScroll}
          columnCount={frozenColumnCount}
          rowCount={props.rowCount - frozenRowCount}
          columnWidth={columnWidth}
          rowHeight={offsetRowHeight}
          className="VirtualizedGrid-LeftGrid"
          cellComponent={OffsetCellRenderer}
          cellProps={frozenColumnCellProps}
          overscanCount={VIRTUALIZED_GRID_OVERSCAN_COUNT}
          dir={direction}
        />
        <div className="VirtualizedGrid-MainGridContainer">
          <div
            className="VirtualizedGrid-MainGridInnerContainer"
            style={mainGridInnerContainerStyles}
          >
            <Grid
              gridRef={setMainGridApi}
              style={{ width: unfrozenWidth, height: unfrozenHeight }}
              columnWidth={offsetColumnWidth}
              rowHeight={offsetRowHeight}
              className="VirtualizedGrid-MainGrid middle_scrollbar"
              onScroll={handleScroll}
              rowCount={props.rowCount - frozenRowCount}
              columnCount={props.columnCount - frozenColumnCount}
              cellComponent={OffsetCellRenderer}
              cellProps={mainCellProps}
              overscanCount={VIRTUALIZED_GRID_OVERSCAN_COUNT}
              dir={direction}
            />
          </div>
        </div>
      </div>
    </VirtualizedGridStyled>
  );
};
